import React, { useState, useEffect } from 'react';
import { 
  Phone, 
  MessageSquare, 
  Calendar, 
  AlertCircle, 
  Search, 
  Filter, 
  ChevronRight, 
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  Clock, 
  DollarSign, 
  CheckCircle2, 
  XCircle,
  FileText,
  User,
  History,
  Mail,
  Edit2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../Config';

export default function Cobranzas() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [morosos, setMorosos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCredito, setSelectedCredito] = useState(null);
  const [showGestionModal, setShowGestionModal] = useState(false);
  const [showHistorialModal, setShowHistorialModal] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [nuevaGestion, setNuevaGestion] = useState({
    tipoGestion: 'LLAMADA',
    resultadoGestion: 'ATENDIO',
    observacion: '',
    fechaPromesa: '',
    montoPrometido: ''
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [goToPage, setGoToPage] = useState('1');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/cobranzas/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMorosos(data);
      }
    } catch (err) {
      console.error("Error fetching cobranzas data", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistorial = async (creditoId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/cobranzas/historial/${creditoId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHistorial(data);
      }
    } catch (err) {
      console.error("Error fetching historial", err);
    }
  };

  const handleRegistrarGestion = async () => {
    try {
      const resGestion = await fetch(`${API_BASE_URL}/cobranzas/gestionar`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          tipoGestion: nuevaGestion.tipoGestion,
          resultadoGestion: nuevaGestion.resultadoGestion,
          observacion: nuevaGestion.observacion,
          fechaPromesa: nuevaGestion.fechaPromesa,
          montoPrometido: nuevaGestion.montoPrometido,
          credito: { id: Number(selectedCredito.creditoId) },
          usuario: user?.id ? { id: Number(user.id) } : null
        })
      });

      if (!resGestion.ok) {
        const errorMsg = await resGestion.text();
        throw new Error(errorMsg || 'Error al registrar la gestión');
      }

      setShowGestionModal(false);
      setNuevaGestion({ 
        tipoGestion: 'LLAMADA', 
        resultadoGestion: 'ATENDIO', 
        observacion: '',
        fechaPromesa: '',
        montoPrometido: ''
      });
      fetchDashboard();
    } catch (err) {
      console.error("Error registering gestion", err);
      alert(err.message);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/cobranzas/exportar-excel`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Reporte_Cobranzas_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (err) {
      console.error("Error downloading excel", err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  const filteredMorosos = morosos.filter(m => 
    m.nombreSocio.toLowerCase().includes(searchTerm.toLowerCase()) || 
    m.numeroDocumento.includes(searchTerm)
  );

  // Pagination logic
  const totalItems = filteredMorosos.length;
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalItems);
  const currentItems = filteredMorosos.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    const page = Math.max(1, Math.min(newPage, totalPages));
    setCurrentPage(page);
    setGoToPage(page.toString());
  };

  const handleGoToPage = (e) => {
    if (e.key === 'Enter') {
      const page = parseInt(goToPage);
      if (!isNaN(page)) {
        handlePageChange(page);
      }
    }
  };

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      {/* Header & Stats */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Recuperaciones y Cobranzas</h1>
        <p className="text-slate-500 font-medium">Gestión estratégica de la cartera en mora</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        {[
          { label: 'Cartera en Mora', value: formatCurrency(morosos.reduce((acc, m) => acc + m.totalVencido, 0)), icon: DollarSign, color: 'blue' },
          { label: 'Socios Morosos', value: morosos.length, icon: User, color: 'orange' },
          { label: 'Mora Proyectada', value: formatCurrency(morosos.reduce((acc, m) => acc + m.totalMora, 0)), icon: AlertCircle, color: 'red' },
          { label: 'Promesas Hoy', value: '12', icon: Clock, color: 'emerald' }
        ].map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
            <div className={`w-12 h-12 rounded-xl bg-${stat.color}-50 flex items-center justify-center mb-4`}>
              <stat.icon className={`text-${stat.color}-600 w-6 h-6`} />
            </div>
            <p className="text-slate-500 text-sm font-medium">{stat.label}</p>
            <h3 className="text-xl font-bold text-slate-900 mt-1">{stat.value}</h3>
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="bg-white border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 bg-white flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Buscar socio por nombre o documento..."
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={handleDownloadExcel}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors shadow-sm"
            >
              <FileText className="w-4 h-4" />
              Descargar Excel
            </button>
            <button 
              onClick={() => {
                fetch(`${API_BASE_URL}/cobranzas/ejecutar-proceso-mora`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}` }
                }).then(() => fetchDashboard());
              }}
              className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors shadow-sm"
            >
              <Clock className="w-4 h-4" />
              Recalcular Mora
            </button>
          </div>
        </div>

        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-slate-200 bg-white">
              <th className="px-4 py-3 w-10"></th>
              <th className="px-4 py-3 text-sm font-bold text-slate-700">Socio / Préstamo</th>
              <th className="px-4 py-3 text-sm font-bold text-slate-700">Contacto</th>
              <th className="px-4 py-3 text-sm font-bold text-slate-700">Atraso</th>
              <th className="px-4 py-3 text-sm font-bold text-slate-700 text-right">Monto Vencido</th>
              <th className="px-4 py-3 text-sm font-bold text-slate-700">Compromiso</th>
              <th className="px-4 py-3 text-sm font-bold text-slate-700">Atendido Por</th>
              <th className="px-4 py-3 text-sm font-bold text-slate-700">Última Gestión</th>
              <th className="px-4 py-3 text-sm font-bold text-slate-700 text-center"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {currentItems.map((m) => (
              <tr key={m.creditoId} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-2">
                  <button 
                    onClick={() => {
                      setSelectedCredito(m);
                      fetchHistorial(m.creditoId);
                      setShowGestionModal(true);
                    }}
                    className="text-slate-400 hover:text-blue-600 transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </td>
                <td className="px-4 py-2">
                  <div className="text-sm text-slate-900">{m.nombreSocio}</div>
                  <div className="text-[11px] text-slate-400">ID: {m.creditoId}</div>
                </td>
                <td className="px-4 py-2">
                  <div className="text-[11px] text-slate-600">{m.telefonoSocio}</div>
                  <div className="text-[11px] text-slate-400">{m.emailSocio}</div>
                </td>
                <td className="px-4 py-2">
                  <span className={`text-xs font-bold ${m.diasAtrasoMaximo > 30 ? 'text-red-600' : 'text-slate-700'}`}>
                    {m.diasAtrasoMaximo} días
                  </span>
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="text-sm font-medium text-slate-900">{formatCurrency(m.totalVencido)}</div>
                  <div className="text-[10px] text-slate-400">{m.cuotasVencidas} cuotas</div>
                </td>
                <td className="px-4 py-2">
                  {m.comprometido === 'S' ? (
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-emerald-600">Comprometido</span>
                      <span className="text-[10px] text-slate-500">{new Date(m.fechaCompromiso).toLocaleDateString()}</span>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-300">Sin compromiso</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <span className="text-[11px] text-slate-600">{m.usuarioCompromiso || '-'}</span>
                </td>
                <td className="px-4 py-2">
                  <div className="text-[11px] text-slate-700 truncate max-w-[150px]">
                    {m.ultimaObservacion || '-'}
                  </div>
                  {m.ultimaFechaGestion && (
                    <div className="text-[10px] text-slate-400">
                      {new Date(m.ultimaFechaGestion).toLocaleDateString()}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 text-center">
                  <button 
                    onClick={() => {
                      setSelectedCredito(m);
                      fetchHistorial(m.creditoId);
                      setShowHistorialModal(true);
                    }}
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <History className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Paginación estilo Premium */}
        <div className="p-4 bg-white border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Ir a</span>
              <input 
                type="text" 
                className="w-12 h-8 border border-slate-200 rounded text-center text-xs font-bold outline-none focus:ring-2 focus:ring-blue-500"
                value={goToPage}
                onChange={(e) => setGoToPage(e.target.value)}
                onKeyDown={handleGoToPage}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Ver</span>
              <select 
                className="h-8 border border-slate-200 rounded text-xs font-bold px-2 outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {[5, 10, 20, 50, 100].map(val => (
                  <option key={val} value={val}>{val}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button 
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === 1 ? 'text-slate-300' : 'text-slate-600'}`}
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === 1 ? 'text-slate-300' : 'text-slate-600'}`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div className="flex items-center mx-2 gap-1">
              {[...Array(totalPages)].map((_, i) => {
                const p = i + 1;
                // Mostrar solo páginas cercanas a la actual si hay muchas
                if (totalPages > 5) {
                  if (p !== 1 && p !== totalPages && Math.abs(p - currentPage) > 1) {
                    if (p === 2 || p === totalPages - 1) return <span key={p} className="text-slate-300">...</span>;
                    return null;
                  }
                }
                return (
                  <button
                    key={p}
                    onClick={() => handlePageChange(p)}
                    className={`w-8 h-8 rounded text-xs font-bold transition-all ${currentPage === p ? 'bg-blue-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'}`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>

            <button 
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === totalPages || totalPages === 0 ? 'text-slate-300' : 'text-slate-600'}`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button 
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === totalPages || totalPages === 0 ? 'text-slate-300' : 'text-slate-600'}`}
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>

          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
            [{totalItems > 0 ? startIndex + 1 : 0} a {endIndex} de {totalItems}]
          </div>
        </div>
      </div>

      {/* Modal de Gestión */}
      {showGestionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <div>
                <h3 className="text-xl font-bold text-slate-900">Gestionar Cobranza</h3>
                <p className="text-sm text-slate-500 font-medium">Socio: {selectedCredito?.nombreSocio}</p>
              </div>
              <button onClick={() => setShowGestionModal(false)} className="p-2 hover:bg-white rounded-full transition-colors shadow-sm">
                <XCircle className="w-6 h-6 text-slate-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                <div>
                  <p className="text-[10px] uppercase font-bold text-blue-600">Total Vencido</p>
                  <p className="text-xl font-black text-blue-900">{formatCurrency(selectedCredito?.totalVencido)}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase font-bold text-blue-600">Interés Punitorio</p>
                  <p className="text-xl font-black text-red-600">{formatCurrency(selectedCredito?.totalMora)}</p>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <History className="w-4 h-4 text-slate-400" />
                  Últimas Interacciones
                </h4>
                <div className="space-y-3">
                  {historial.length === 0 ? (
                    <p className="text-sm text-slate-400 italic bg-slate-50 p-4 rounded-xl text-center">No hay gestiones registradas aún.</p>
                  ) : (
                    historial.slice(0, 3).map((h, i) => (
                      <div key={i} className="p-3 bg-white border border-slate-100 rounded-xl shadow-sm">
                        <div className="flex justify-between items-start mb-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600">
                            {h.tipoGestion}
                          </span>
                          <span className="text-[10px] text-slate-400 font-medium">{new Date(h.createdAt).toLocaleString()}</span>
                        </div>
                        <p className="text-sm text-slate-700 font-medium">{h.observacion}</p>
                        {h.fechaCompromiso && (
                          <div className="mt-2 p-2 bg-amber-50 border border-amber-100 rounded-lg text-[10px] text-amber-700 font-bold">
                            Compromiso: {new Date(h.fechaCompromiso).toLocaleDateString()} - {formatCurrency(h.montoComprometido)}
                          </div>
                        )}
                        <p className="text-[10px] text-slate-400 mt-2 italic">Resultado: {h.resultadoGestion}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-slate-100">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Medio de Contacto</label>
                    <select 
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                      value={nuevaGestion.tipoGestion}
                      onChange={(e) => setNuevaGestion({...nuevaGestion, tipoGestion: e.target.value})}
                    >
                      <option value="LLAMADA">Llamada Telefónica</option>
                      <option value="WHATSAPP">WhatsApp</option>
                      <option value="VISITA">Visita Domiciliaria</option>
                      <option value="EMAIL">Correo Electrónico</option>
                      <option value="CARTA_COLACION">Carta Colacionada</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Resultado</label>
                    <select 
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                      value={nuevaGestion.resultadoGestion}
                      onChange={(e) => setNuevaGestion({...nuevaGestion, resultadoGestion: e.target.value})}
                    >
                      <option value="ATENDIO">Atendió / Contacto</option>
                      <option value="NO_ATENDIO">No Atendió</option>
                      <option value="PROMETIO_PAGO">Prometió Pago</option>
                      <option value="RECHAZO_PAGO">Rechaza Pago</option>
                      <option value="EQUIVOCADO">Número Equivocado</option>
                    </select>
                  </div>
                </div>

                {nuevaGestion.resultadoGestion === 'PROMETIO_PAGO' && (
                  <div className="grid grid-cols-2 gap-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                    <div>
                      <label className="block text-[10px] font-black text-amber-600 uppercase mb-1">Fecha de la Promesa</label>
                      <input 
                        type="date"
                        className="w-full p-2.5 bg-white border border-amber-200 rounded-xl outline-none focus:ring-2 focus:ring-amber-500 font-bold text-slate-800"
                        value={nuevaGestion.fechaPromesa}
                        onChange={(e) => setNuevaGestion({...nuevaGestion, fechaPromesa: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-amber-600 uppercase mb-1">Monto Prometido (Gs.)</label>
                      <input 
                        type="number"
                        className="w-full p-2.5 bg-white border border-amber-200 rounded-xl outline-none focus:ring-2 focus:ring-amber-500 font-bold text-slate-800"
                        placeholder={selectedCredito?.totalVencido}
                        value={nuevaGestion.montoPrometido}
                        onChange={(e) => setNuevaGestion({...nuevaGestion, montoPrometido: e.target.value})}
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1">Observaciones / Detalles</label>
                  <textarea 
                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 h-24 resize-none font-medium text-slate-700"
                    placeholder="Escriba aquí los detalles de la conversación..."
                    value={nuevaGestion.observacion}
                    onChange={(e) => setNuevaGestion({...nuevaGestion, observacion: e.target.value})}
                  ></textarea>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50 flex gap-3">
              <button 
                onClick={() => setShowGestionModal(false)}
                className="flex-1 py-3 text-slate-600 font-bold hover:bg-white rounded-xl transition-all"
              >
                Cancelar
              </button>
              <button 
                onClick={handleRegistrarGestion}
                className="flex-[2] py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all flex items-center justify-center gap-2"
              >
                <CheckCircle2 className="w-5 h-5" />
                Registrar Gestión
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Historial Completo */}
      {showHistorialModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <div>
                <h3 className="text-xl font-bold text-slate-900">Historial de Gestiones</h3>
                <p className="text-sm text-slate-500 font-medium">Socio: {selectedCredito?.nombreSocio}</p>
              </div>
              <button onClick={() => setShowHistorialModal(false)} className="p-2 hover:bg-white rounded-full transition-colors">
                <XCircle className="w-6 h-6 text-slate-400" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {historial.map((h, i) => (
                  <div key={i} className="p-4 bg-white border border-slate-200 rounded-xl hover:border-blue-200 transition-colors">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-600 border border-blue-100 uppercase">
                          {h.tipoGestion}
                        </span>
                        <span className="text-xs font-bold text-slate-700">{h.resultadoGestion}</span>
                      </div>
                      <span className="text-xs text-slate-400 font-medium">{new Date(h.createdAt).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed mb-3">{h.observacion}</p>
                    {h.fechaCompromiso && (
                      <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-amber-600" />
                          <span className="text-xs font-bold text-amber-700">Compromiso: {new Date(h.fechaCompromiso).toLocaleDateString()}</span>
                        </div>
                        <span className="text-xs font-black text-amber-600">{formatCurrency(h.montoComprometido)}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
